import { isSameDayAheadBidValue } from 'helpers/elplan.helper/bidValue.helper'
import { useEffect, useRef, useState } from 'react'
import type { RowValues } from 'ui/components/HourlyDataTable/HourlyDataTable.types'
import type { DayAheadBidTableResetController } from './DayAheadBidTable.types'

type UseDayAheadBidTableEditsResult = {
  values: RowValues[]
  setValues: React.Dispatch<React.SetStateAction<RowValues[]>>
  valuesRef: React.MutableRefObject<RowValues[]>
  isDirty: boolean
  isCellUnsaved: (rowIndex: number, cellKey: string) => boolean
  revert: () => void
  commit: (submittedValues?: RowValues[]) => void
}

function cloneValues(values: RowValues[]): RowValues[] {
  return values.map((row) => ({ ...row }))
}

function createEmptyValues(values: RowValues[]): RowValues[] {
  return values.map((row) => Object.fromEntries(Object.keys(row).map((key) => [key, ''])))
}

function areValuesEqual(left: RowValues[], right: RowValues[], textRowIndex?: number): boolean {
  if (left.length !== right.length) {
    return false
  }

  return left.every((row, rowIndex) => {
    const baselineRow = right[rowIndex] ?? {}
    const keys = new Set([...Object.keys(row), ...Object.keys(baselineRow)])

    return [...keys].every((key) =>
      rowIndex === textRowIndex
        ? (row[key] ?? '') === (baselineRow[key] ?? '')
        : isSameDayAheadBidValue(row[key], baselineRow[key])
    )
  })
}

export function useDayAheadBidTableEdits(
  baselineValues: RowValues[],
  textRowIndex: number | undefined,
  tableResetController: DayAheadBidTableResetController,
  tableContextKey: string
): UseDayAheadBidTableEditsResult {
  const [committedValues, setCommittedValues] = useState(() => cloneValues(baselineValues))
  const [values, setValues] = useState(() => cloneValues(baselineValues))
  const valuesRef = useRef(values)
  const baselineValuesRef = useRef(baselineValues)
  const previousBaselineRef = useRef(cloneValues(baselineValues))
  const previousContextKeyRef = useRef(tableContextKey)
  const pendingBaselineRef = useRef<RowValues[] | null>(null)

  useEffect(() => {
    valuesRef.current = values
    baselineValuesRef.current = baselineValues
  })

  useEffect(() => {
    function reset(): void {
      const currentBaseline = baselineValuesRef.current
      pendingBaselineRef.current = null
      previousBaselineRef.current = cloneValues(currentBaseline)
      previousContextKeyRef.current = tableContextKey
      const emptyValues = createEmptyValues(currentBaseline)
      valuesRef.current = emptyValues
      setCommittedValues(emptyValues)
      setValues(emptyValues)
    }

    return tableResetController.register(tableContextKey, reset)
  }, [tableContextKey, tableResetController])

  const isDirty = !areValuesEqual(values, committedValues, textRowIndex)

  function isCellUnsaved(rowIndex: number, cellKey: string): boolean {
    return rowIndex === textRowIndex
      ? (values[rowIndex]?.[cellKey] ?? '') !== (committedValues[rowIndex]?.[cellKey] ?? '')
      : !isSameDayAheadBidValue(values[rowIndex]?.[cellKey], committedValues[rowIndex]?.[cellKey])
  }

  useEffect(() => {
    const baselineChanged = !areValuesEqual(baselineValues, previousBaselineRef.current, textRowIndex)
    const contextChanged = tableContextKey !== previousContextKeyRef.current
    if (baselineChanged || contextChanged) {
      previousBaselineRef.current = cloneValues(baselineValues)
      previousContextKeyRef.current = tableContextKey
      if (isDirty) {
        pendingBaselineRef.current = cloneValues(baselineValues)
        return
      }

      pendingBaselineRef.current = null
      const nextBaseline = cloneValues(baselineValues)
      setCommittedValues(nextBaseline)
      setValues(nextBaseline)
      return
    }

    if (isDirty || !pendingBaselineRef.current) {
      return
    }

    const nextBaseline = pendingBaselineRef.current
    pendingBaselineRef.current = null
    setCommittedValues(nextBaseline)
    setValues(nextBaseline)
  }, [baselineValues, isDirty, tableContextKey, textRowIndex])

  function revert(): void {
    setValues(cloneValues(committedValues))
  }

  function commit(submittedValues = valuesRef.current): void {
    const nextBaseline = cloneValues(submittedValues)
    setCommittedValues(nextBaseline)
    if (areValuesEqual(valuesRef.current, submittedValues, textRowIndex)) {
      setValues(nextBaseline)
    }
  }

  return { values, setValues, valuesRef, isDirty, isCellUnsaved, revert, commit }
}
